import { useState, useEffect, useRef, useContext } from "react";
import ReactDOM from "react-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import {
  CustomDragAndDrop,
  handleFileSelect,
} from "../../../Common/Common-Function/CommonFunctions.js";
import { get, post } from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  GET_JOB_DETAILS_BY_ID,
  GET_CLIENT_DETAILS,
  EMAIL_CLIENT,
  EMAIL_CLIENT_PREVIEW,
} from "../../../helper/url_helper";
import TextInput from "../../../Common/Textfield/Textfield";
import TextArea from "../../../Common/TextArea/TextArea";
import Button from "../../../Common/Button/Button";
import SendJobBlastIcon from "../../../Assets/Images/sendJobBlast.svg";
import {
  SidePopup,
  StandardPopup,
} from "../../../Common/PopupStyles/PopupComponent";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import mailIcon from "../../../Assets/Images/mail_icon.svg";
import candidateIcon from "../../../Assets/Images/purple-candidate-icon.svg";
import clientIcon from "../../../Assets/Images/purple-client-icon.svg";
import jobsIcon from "../../../Assets/Images/purple-jobs-icon.svg";
import EmailPreviewPopup from "../../../Common/EmailPreviewPopup/EmailPreviewPopup";
import * as StyleDOM from "./CandidateDetails/CandidateDocuments.jsx";
import FileDropIcon from "../../../Assets/Images/upload.svg";
import { ReactComponent as DeleteIcon } from "../../../Assets/Images/trash.svg";
import { DynamicContext } from "../../../Context/DynamicContext.js";

const ALLOWED_ATTACHMENT_FORMATS = [
  ".pdf",
  ".docx",
  ".doc",
  ".txt",
  ".csv",
  ".xlsx",
  ".xls",
];
const ACCEPTED_ATTACHMENT_FORMATS = ALLOWED_ATTACHMENT_FORMATS.join(",");

function ClientEmailPopup({
  isOpen,
  onClose,
  selectedCandidates = [],
  jobId,
  jobTitle,
}) {
  const { isHiringTeamTenant } = useContext(DynamicContext);
  const tenantLabel = isHiringTeamTenant ? "Hiring Team" : "Client";
  const [isSending, setIsSending] = useState(false);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [contactPersons, setContactPersons] = useState([]);
  const [selectedContactPersons, setSelectedContactPersons] = useState([]);
  const [previewData, setPreviewData] = useState({
    body: "",
    subject: "",
    isLoading: false,
  });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const fileInputRef = useRef(null);
  const clientEmailFormik = useFormik({
    initialValues: {
      selectedJob: jobId && jobTitle ? { label: jobTitle, value: jobId } : null,
      clientName: "",
      subject: "",
      message: ``,
      jobDescription: "",
    },
    validationSchema: Yup.object({
      selectedJob: Yup.object().nullable().required("Job is required"),
      clientName: Yup.string().required("Client name is required"),
      subject: Yup.string().required("Subject is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values) => {
      if (!clientEmailFormik.values.selectedJob?.value) {
        Toast("error", "A job must be associated with this email.");
        return;
      }

      if (selectedContactPersons.length === 0) {
        Toast("error", "Please select at least one contact person");
        return;
      }

      setIsSending(true);
      try {
        const payload = {
          job_id: values.selectedJob?.value,
          client_recipient_emails: selectedContactPersons.map((c) => c.email),
          candidate_ids: selectedCandidates.map((c) => c.id),
          subject: values.subject,
          message: values.message,
        };
        const formData = new FormData();
        formData.append("request", JSON.stringify(payload));
        attachmentFiles.forEach((file) => {
          formData.append("attachments", file);
        });
        await post(VYLABS_BASE_URL, EMAIL_CLIENT, formData);
        Toast("success", `Email sent to client successfully`);
        onClose();
      } catch (error) {
        console.error("Error sending client email:", error);
        Toast(
          "error",
          error.message || "Failed to send email. Please try again.",
        );
      } finally {
        setIsSending(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      clientEmailFormik.resetForm();
      setContactPersons([]);
      setSelectedContactPersons([]);
      setIsSending(false);
      setIsEmailPreviewOpen(false);
      setPreviewData({ body: "", subject: "", isLoading: false });
      setAttachmentFiles([]);
    }
  }, [isOpen]);

  const messageWrapperRef = useRef(null);
  useEffect(() => {
    const textarea = messageWrapperRef.current?.querySelector("textarea");
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [clientEmailFormik.values.message]);

  useEffect(() => {
    if (jobId && jobTitle && isOpen) {
      const preselectedJob = { label: jobTitle, value: jobId };
      clientEmailFormik.setFieldValue("selectedJob", preselectedJob);
      fetchJobAndClientDetails(jobId);
    }
  }, [jobId, jobTitle, isOpen]);

  const fetchJobAndClientDetails = async (selectedJobId) => {
    try {
      const jobResponse = await get(
        VYLABS_BASE_URL,
        `${GET_JOB_DETAILS_BY_ID}?id=${selectedJobId}`,
      );
      const job = jobResponse?.job;
      const jobDescription = job?.description;
      clientEmailFormik.setFieldValue("jobDescription", jobDescription || "");

      if (job?.client_id) {
        const clientResponse = await get(
          VYLABS_BASE_URL,
          `${GET_CLIENT_DETAILS}/?id=${job.client_id}`,
        );
        const client = clientResponse?.client;
        if (client) {
          clientEmailFormik.setFieldValue("clientName", client.name || "");
          const rawPersons = [];

          if (client.contact_person && client.email) {
            rawPersons.push({
              name: client.contact_person,
              email: client.email,
            });
          }

          if (client.contacts && Array.isArray(client.contacts)) {
            rawPersons.push(
              ...client.contacts.filter((c) => c.name && c.email),
            );
          }
          const uniquePersons = Array.from(
            new Map(rawPersons.map((p) => [p.email.toLowerCase(), p])).values(),
          );

          setContactPersons(uniquePersons);

          if (uniquePersons.length === 1) {
            setSelectedContactPersons([uniquePersons[0]]);
          } else {
            setSelectedContactPersons([]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching job/client details:", error);
    }
  };

  const fetchEmailPreview = async () => {
    const { selectedJob, subject, message } = clientEmailFormik.values;
    setPreviewData((prev) => ({ ...prev, isLoading: true }));
    try {
      const payload = {
        job_id: selectedJob.value,
        candidate_ids: selectedCandidates.map((c) => c.id),
        subject: subject,
        message: message,
      };
      const formData = new FormData();
      formData.append("request", JSON.stringify(payload));
      const response = await post(
        VYLABS_BASE_URL,
        EMAIL_CLIENT_PREVIEW,
        formData,
      );
      setPreviewData({
        body: response.body || "",
        subject: response.subject || subject,
        isLoading: false,
      });
      setIsEmailPreviewOpen(true);
    } catch (error) {
      console.error("Error fetching email preview:", error);
      Toast(
        "error",
        error.message || "Failed to generate email preview. Please try again.",
      );
      setPreviewData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleEmailPreviewClick = async () => {
    if (!canAccessPreview()) {
      return;
    }
    await fetchEmailPreview();
  };

  const handleContactPersonToggle = (person) => {
    setSelectedContactPersons((prev) => {
      const isSelected = prev.some((p) => p.email === person.email);
      if (isSelected) {
        return prev.filter((p) => p.email !== person.email);
      } else {
        return [...prev, person];
      }
    });
  };

  const isContactPersonSelected = (person) => {
    return selectedContactPersons.some((p) => p.email === person.email);
  };

  const canAccessPreview = () => {
    const { selectedJob, clientName, subject, message } =
      clientEmailFormik.values;
    return (
      selectedJob &&
      selectedJob.value &&
      clientName?.trim() &&
      subject?.trim() &&
      message?.trim() &&
      selectedContactPersons.length > 0 &&
      selectedCandidates.length > 0
    );
  };

  const canSendEmail = canAccessPreview();
  const isDisabled = isSending || !canSendEmail;

  const formatJobLabel = (label) => {
    if (!label) return "N/A";
    const chunks = label.match(/.{1,60}/g);
    return chunks ? chunks.join("\n") : label;
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 25;
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  const allowedFiles = (files) => {
    const validFiles = handleFileSelect(
      files,
      ALLOWED_ATTACHMENT_FORMATS,
      MAX_FILE_SIZE_MB,
    );

    if (validFiles.length === 0) {
      return [];
    }

    const existingSize = attachmentFiles.reduce(
      (total, file) => total + file.size,
      0,
    );

    const newFilesSize = validFiles.reduce(
      (total, file) => total + file.size,
      0,
    );

    if (existingSize + newFilesSize > MAX_TOTAL_SIZE_BYTES) {
      Toast(
        "warning",
        `The total size of all files cannot exceed ${MAX_TOTAL_SIZE_MB} MB.`,
      );
      return [];
    }

    return validFiles;
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = allowedFiles(files);

    if (newFiles.length > 0) {
      setAttachmentFiles((prev) => [...prev, ...newFiles]);
    }

    e.target.value = "";
  };

  const handleDropFiles = (event) => {
    event.preventDefault();

    const files = Array.from(event.dataTransfer.files);
    const newFiles = allowedFiles(files);

    if (newFiles.length > 0) {
      setAttachmentFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleAttachmentDelete = (fileToDelete) => {
    setAttachmentFiles((prev) => prev.filter((file) => file !== fileToDelete));
  };

  const { isDragging, handleDragEnter, handleDragOver, handleDragLeave } =
    CustomDragAndDrop(handleDropFiles, false);

  return (
    <SidePopup
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={mailIcon} alt="" style={{ width: "15px" }} />
          <span style={{ color: "var(--header__color)" }}>
            Email Candidate Profiles to {tenantLabel}
          </span>
        </div>
      }
      width="70%"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          backgroundColor: "var(--body__background)",
          padding: "20px",
        }}
      >
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}
        >
          <div>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <img src={jobsIcon} alt="" style={{ width: "15px" }} />
              <span>Selected Job</span>
            </label>
            <div
              style={{
                minHeight: "40px",
                padding: "8px 12px",
                marginTop: "5px",
                display: "flex",
                alignItems: "center",
                color: "var(--header__color)",
                whiteSpace: "pre-wrap",
              }}
            >
              {formatJobLabel(clientEmailFormik.values.selectedJob?.label)}
            </div>
          </div>
          <div>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <img src={clientIcon} alt="" style={{ width: "15px" }} />
              <span>{tenantLabel}</span>
            </label>
            <div
              style={{
                height: "40px",
                padding: "0 12px",
                marginTop: "5px",
                display: "flex",
                alignItems: "center",
                color: "var(--header__color)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {clientEmailFormik.values.clientName || "N/A"}
            </div>
          </div>
        </div>
        {contactPersons.length > 0 && (
          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "10px",
              }}
            >
              <img src={candidateIcon} alt="" style={{ width: "15px" }} />
              <span>Contact Person</span>
              {selectedContactPersons.length === 0 && (
                <span
                  style={{
                    color: "#6B7280",
                    fontSize: "12px",
                    marginLeft: "auto",
                  }}
                >
                  Select at least one contact to continue
                </span>
              )}
            </label>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {contactPersons.map((person, index) => (
                <div
                  key={index}
                  onClick={() => handleContactPersonToggle(person)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px",
                    border: `1px solid ${
                      isContactPersonSelected(person)
                        ? "#6860ee"
                        : "var(--border__color)"
                    }`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: isContactPersonSelected(person)
                      ? "rgba(104, 96, 238, 0.05)"
                      : "transparent",
                    transition: "all 0.2s",
                  }}
                >
                  {isContactPersonSelected(person) ? (
                    <CheckBoxIcon
                      style={{ color: "#6860ee", fontSize: "20px" }}
                    />
                  ) : (
                    <CheckBoxOutlineBlankIcon
                      style={{
                        color: "var(--border__color)",
                        fontSize: "20px",
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{ fontWeight: 500, color: "var(--header__color)" }}
                    >
                      {person.name}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B7280" }}>
                      {person.email}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src={mailIcon} alt="" style={{ width: "15px" }} />
            <span
              style={{
                fontSize: "16px",
                fontWeight: 500,
                color: "var(--header__color)",
              }}
            >
              Email Content
            </span>
          </label>
          <div style={{ marginTop: "12px" }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span style={{ color: "#6B7280" }}>Subject</span>
            </label>
            <TextInput
              name="subject"
              placeholder="Candidate Profiles for Selected Position - VybogTAL"
              value={clientEmailFormik.values.subject}
              onChange={clientEmailFormik.handleChange}
              onBlur={clientEmailFormik.handleBlur}
              error={
                clientEmailFormik.touched.subject &&
                Boolean(clientEmailFormik.errors.subject)
              }
              errorMessage={clientEmailFormik.errors.subject}
              style={{
                border: "1px solid var(--border__color)",
                marginTop: "5px",
              }}
            />
          </div>
          <div style={{ marginTop: "14px" }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span style={{ color: "#6B7280" }}>Message</span>
            </label>
            <div ref={messageWrapperRef} style={{ marginTop: "5px" }}>
              <TextArea
                rows={6}
                name="message"
                placeholder="Enter your message to the client about the candidates..."
                value={clientEmailFormik.values.message}
                onChange={clientEmailFormik.handleChange}
                onBlur={clientEmailFormik.handleBlur}
                style={{
                  border: "1px solid var(--border__color)",
                  overflow: "hidden",
                  resize: "none",
                }}
              />
            </div>
            {clientEmailFormik.touched.message &&
              clientEmailFormik.errors.message && (
                <div
                  style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                >
                  {clientEmailFormik.errors.message}
                </div>
              )}
          </div>
          <div style={{ marginTop: "14px" }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span>Attachment</span>
            </label>
            <div style={{ marginTop: "8px" }}>
              <StyleDOM.DocumentLeftContainer
                className="documentleft-container"
                style={{ flexDirection: "column" }}
              >
                <StyleDOM.FileDropMainContainer
                  className="filedropmain-container"
                  style={{ width: "100%" }}
                >
                  <StyleDOM.FileDropContainer
                    onClick={handleFileUploadClick}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropFiles}
                    isDragging={isDragging}
                    className="FileDrop-container"
                  >
                    <img src={FileDropIcon} alt="File-uploadicon" />
                    <StyleDOM.FileDropMessage>
                      {isDragging
                        ? "Drop your files here"
                        : "Drag and Drop your files"}
                    </StyleDOM.FileDropMessage>

                    <StyleDOM.NoteListContainer className="NoteList-container">
                      <StyleDOM.NoteList>
                        Each file must not exceed 10 MB
                      </StyleDOM.NoteList>
                      <StyleDOM.NoteList>
                        You can upload multiple files, up to 25 MB in total
                      </StyleDOM.NoteList>
                      <StyleDOM.NoteList>
                        Supported formats: PDF, DOC, DOCX, TXT, CSV, XLSX, XLS
                      </StyleDOM.NoteList>
                    </StyleDOM.NoteListContainer>

                    <input
                      accept={ACCEPTED_ATTACHMENT_FORMATS}
                      multiple
                      name="attachmentFiles"
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileInputChange}
                    />
                  </StyleDOM.FileDropContainer>
                </StyleDOM.FileDropMainContainer>

                {attachmentFiles.length > 0 && (
                  <StyleDOM.UploadedFilesContainer style={{ width: "100%" }}>
                    {attachmentFiles.map((file) => (
                      <StyleDOM.UploadedFileItem
                        key={`${file.name}-${file.lastModified}`}
                      >
                        <StyleDOM.FileNameContainer>
                          {file.name}
                        </StyleDOM.FileNameContainer>
                        <StyleDOM.DeleteIconDiv
                          onClick={() => handleAttachmentDelete(file)}
                        >
                          <DeleteIcon />
                        </StyleDOM.DeleteIconDiv>
                      </StyleDOM.UploadedFileItem>
                    ))}
                  </StyleDOM.UploadedFilesContainer>
                )}
              </StyleDOM.DocumentLeftContainer>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "14px",
            marginTop: "20px",
          }}
        >
          <Button
            style={{ marginRight: "0px" }}
            label="Cancel"
            isCancel
            event={onClose}
          />
          <CustomTooltip
            title={
              canSendEmail
                ? "Click to view preview"
                : "Fill all required fields to view preview"
            }
            arrow
          >
            <span>
              <Button
                label={
                  previewData.isLoading ? "Generating..." : "Email Preview"
                }
                event={handleEmailPreviewClick}
                backgroundColor="#6860ee"
                color="#FFFFFF"
                disabled={!canSendEmail || previewData.isLoading}
                style={{ height: "42px" }}
                {...((!canSendEmail || previewData.isLoading) && {
                  hoverd: false,
                  hoverStyles: {},
                  hoverIconSrc: null,
                  style: {
                    backgroundColor: "var(--disabled-button)",
                    color: "white",
                    cursor: "not-allowed",
                    opacity: 0.7,
                    height: "42px",
                  },
                })}
              />
            </span>
          </CustomTooltip>
          <Button
            label={isSending ? "Sending Email..." : "Send Email"}
            iconSrc={SendJobBlastIcon}
            event={clientEmailFormik.handleSubmit}
            disabled={isDisabled}
            {...(isDisabled && {
              hoverd: false,
              hoverStyles: {},
              hoverIconSrc: null,
              style: {
                backgroundColor: "var(--disabled-button)",
                color: "white",
                cursor: "not-allowed",
                opacity: 0.7,
              },
            })}
          />
        </div>
        {ReactDOM.createPortal(
          <StandardPopup
            isOpen={isEmailPreviewOpen}
            onClose={() => {
              setIsEmailPreviewOpen(false);
              setPreviewData({ body: "", subject: "", isLoading: false });
            }}
            title="Email Preview"
            hideActions={true}
            width="900px"
            maxHeight="95vh"
            popupStyle={{
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            contentStyle={{
              padding: 0,
              marginTop: 0,
            }}
          >
            <div style={{ overflow: "hidden" }}>
              <EmailPreviewPopup htmlContent={previewData.body} />
            </div>
          </StandardPopup>,
          document.body,
        )}
      </div>
    </SidePopup>
  );
}

export default ClientEmailPopup;
